import React, { useEffect, useState } from "react"
import { Modal, Typography, List, Checkbox } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash, faUsers } from "@fortawesome/free-solid-svg-icons"

import { useProjectMembersQuery } from "features/Projects/api/useProjectsQuery"
import {
  useAddProjectMemberMutation,
  useRemoveProjectMemberMutation,
} from "features/Projects/api/useProjectsMutations"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"

import { useProjectStore } from "stores/projectStore"

const { Title } = Typography

interface ProjectMembersModalProps {
  userData: string[]
}

/**
 * Modal component for managing project members.
 *
 * This component displays a list of users and allows adding/removing them as project members.
 *
 * @param props - The component props
 * @param props.userData - Array of available users in the unit
 * @returns The rendered project members modal component.
 */
const ProjectMembersModal: React.FC<ProjectMembersModalProps> = ({ userData }) => {
  const project = useProjectStore((state) => state.project)

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [displayUserData, setDisplayUserData] = useState<string[]>([])

  // Loads when the modal opens; add/remove mutations invalidate the key.
  const membersQuery = useProjectMembersQuery(project.id, isModalVisible)
  const projectMembers = membersQuery.data ?? []
  const addMemberMutation = useAddProjectMemberMutation()
  const removeMemberMutation = useRemoveProjectMemberMutation()

  // Prepare user data with project owner first
  useEffect(() => {
    if (userData.length > 0) {
      const userArray = [
        project.Created_by,
        ...userData.filter(
          (user: string) =>
            user.toLowerCase() !== project.Created_by.toLowerCase()
        ),
      ]
      setDisplayUserData(userArray)
    }
  }, [userData, project.Created_by])

  /**
   * Adds selected members to the project. Notifications are handled by the
   * mutation; a failure for one member does not stop the rest.
   * @async
   * @function addMember
   * @returns {Promise<void>} A promise that resolves when all selected members are processed.
   */
  const addMember = async (): Promise<void> => {
    for (const member of selectedMembers) {
      if (projectMembers.includes(member)) {
        notify.error(
          "Member Already Exists!",
          `Member ${member} is already a part of the project.`
        )
        continue
      }

      try {
        await addMemberMutation.mutateAsync({ id: project.id, member })
      } catch (error) {
        console.error("Error Adding Members:", (error as Error).message)
      }
    }
    // Clear selected members after processing
    setSelectedMembers([])
  }

  /**
   * Removes a member from the project. Notifications and the member-list
   * refresh are handled by the mutation.
   * @async
   * @function removeMember
   * @param {string} memberName - The name of the member to remove.
   * @returns {Promise<void>} A promise that resolves when the member is removed.
   */
  const removeMember = async (memberName: string): Promise<void> => {
    try {
      await removeMemberMutation.mutateAsync({
        id: project.id,
        member: memberName,
      })
    } catch (error) {
      console.error("Error Removing Member:", (error as Error).message)
    }
  }

  /**
   * Shows the modal.
   * @function showModal
   */
  const showModal = (): void => {
    setIsModalVisible(true)
  }

  /**
   * Hides the modal.
   * @function cancel
   */
  const cancel = (): void => {
    setIsModalVisible(false)
    // Clear selected members when canceling
    setSelectedMembers([])
  }

  /**
   * Handles the selection of a project member.
   *
   * @function handleMemberSelect
   * @param {string} selectedMember - The selected member string.
   * @returns {Function} The event handler function.
   */
  const handleMemberSelect = (selectedMember: string) => (): void => {
    // Prevent the project creator from being added to the selected members
    if (selectedMember === project.Created_by) {
      return
    }

    setSelectedMembers((prevSelected: string[]) =>
      prevSelected.includes(selectedMember)
        ? prevSelected.filter((member: string) => member !== selectedMember)
        : [...prevSelected, selectedMember]
    )
  }

  /**
   * Handles clicking the trash icon to remove a member
   * @param {React.MouseEvent} e - The click event
   * @param {string} memberName - The name of the member to remove
   */
  const handleRemoveMember = (
    e: React.MouseEvent,
    memberName: string
  ): void => {
    e.stopPropagation()
    removeMember(memberName)
  }

  /**
   * Handles checkbox click events
   * @param {React.MouseEvent} e - The click event
   * @param {string} member - The member name
   */
  const handleCheckboxClick = (e: React.MouseEvent, member: string): void => {
    e.stopPropagation()
    handleMemberSelect(member)()
  }

  return (
    <>
      <DataQButton onClick={showModal} type="primary">
        Members <FontAwesomeIcon className="ml-2" icon={faUsers} />
      </DataQButton>
      <Modal
        open={isModalVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
      >
        <Title style={{ marginTop: "0.5em" }} level={4}>
          Project Members
        </Title>
        <List
          itemLayout="horizontal"
          dataSource={displayUserData}
          renderItem={(member: string) => {
            const isMember: boolean = projectMembers.includes(member)
            const isSelected: boolean = selectedMembers.includes(member)
            const isProjectOwner: boolean = project.Created_by === member

            return (
              <List.Item
                onClick={handleMemberSelect(member)}
                className="cursor-pointer hover:bg-gray-200 rounded-2xl"
                actions={[
                  <div key={member}>
                    {isMember && !isProjectOwner && (
                      <FontAwesomeIcon
                        onClick={(e: React.MouseEvent) =>
                          handleRemoveMember(e, member)
                        }
                        icon={faTrash}
                        className="mr-2 cursor-pointer hover:text-red-500"
                      />
                    )}
                    <Checkbox
                      className="mr-2"
                      onClick={(e: React.MouseEvent) =>
                        handleCheckboxClick(e, member)
                      }
                      checked={isMember || isSelected}
                      disabled={isProjectOwner}
                    />
                  </div>,
                ]}
              >
                <List.Item.Meta
                  className="p-2 ml-2"
                  title={
                    <span>
                      {member}
                      {isProjectOwner && (
                        <span className="ml-2 text-xs text-gray-500">
                          (Owner)
                        </span>
                      )}
                    </span>
                  }
                />
              </List.Item>
            )
          }}
        />
        <div className="flex flex-row mt-5">
          <DataQButton
            onClick={addMember}
            className="ml-auto mr-2"
            type="primary"
            isDisabled={selectedMembers.length === 0}
          >
            Add Member{selectedMembers.length > 1 ? "s" : ""}
            {selectedMembers.length > 0 && (
              <span className="ml-1">({selectedMembers.length})</span>
            )}
          </DataQButton>
          <DataQButton type="secondary" onClick={cancel}>
            Cancel
          </DataQButton>
        </div>
      </Modal>
    </>
  )
}

export default ProjectMembersModal
